import React, { useState, useEffect } from 'react';
import { getSettings, saveSettings } from '../services/settingsService';
import { getTenant } from '../services/tenantService';
import { getPlatformConfig } from '../services/platformConfigService';
import { useSubscription } from '../hooks/useSubscription';
import { useFirebase } from '../contexts/FirebaseContext';
import { Scale, CreditCard, Banknote, ShieldCheck, CheckCircle2, AlertCircle, Percent, Lock, Info, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';

interface Props {
  tenantId: string;
  initialSubTab?: 'pos' | 'payroll' | 'compliance' | 'fees';
}

export const SettingsForm: React.FC<Props> = ({ tenantId, initialSubTab = 'pos' }) => {
  const { user } = useFirebase();
  const navigate = useNavigate();
  const sub = useSubscription(tenantId);
  const [activeSubTab, setActiveSubTab] = useState<'pos' | 'payroll' | 'compliance' | 'fees'>(initialSubTab);
  const [tenantState, setTenantState] = useState<string>('');
  const [formData, setFormData] = useState({
    payoutMethod: 'Paycheck',
    allowPayoutOverride: false,
    payCycle: 'Weekly',
    payDay: '',
    payDayDue: '',
    posTipHandling: 'Reported',
    payrollProvider: '',
    posMerchantProvider: '',
    displayTipBreakdown: true,
    allocationMethod: 'Standard',
    timezone: 'UTC',
    usePosShiftDataForActuals: true,
    isPosTipAllocationRequired: false,
    requireIRS8PercentCompliance: false,
    irs8027Cadence: 'monthly' as 'pay_cycle' | 'monthly' | 'quarterly' | 'annually',
    businessIndustry: 'Restaurant',
    employeeCountOver10: false,
    filedForm8027LastYear: false,
    customComplianceRate: 0.08,
    anchorDate: '',
    enableServiceChargeDistribution: true,
    serviceChargeEmployeeShare: 100,
    serviceChargeAllocationMethod: 'direct_server' as 'direct_server' | 'shift_pool',
    enableMerchantFeeDeduction: false,
    merchantFeeRate: 2.25
  });
  const [payoutMethods, setPayoutMethods] = useState<string[]>(['Paycheck', 'Cash', 'Zelle', 'PayPal/Venmo', 'Other']);
  const [payCycles, setPayCycles] = useState<string[]>(['Weekly', 'Bi-Weekly']);
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // States prohibiting credit card tip fee deductions (e.g., CA Labor Code § 351, MA, ME)
  const isStateProhibitingFeeDeduction = tenantState.toUpperCase() === 'CA' || tenantState.toUpperCase() === 'MA' || tenantState.toUpperCase() === 'ME';

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const [settings, tenantDoc] = await Promise.all([
          getSettings(tenantId),
          getTenant(tenantId)
        ]);

        if (tenantDoc?.state) {
          setTenantState(tenantDoc.state.trim().toUpperCase());
        }

        console.log('DEBUG [SettingsForm] Loaded settings:', settings);
        if (settings) {
          setFormData(prev => ({ 
            ...prev, 
            ...settings,
            posTipHandling: settings.posTipHandling || 'Reported',
            requireIRS8PercentCompliance: settings.requireIRS8PercentCompliance !== undefined ? settings.requireIRS8PercentCompliance : false,
            irs8027Cadence: settings.irs8027Cadence || 'monthly',
            businessIndustry: settings.businessIndustry || 'Restaurant',
            employeeCountOver10: settings.employeeCountOver10 !== undefined ? settings.employeeCountOver10 : false,
            filedForm8027LastYear: (settings as any).filedForm8027LastYear !== undefined ? (settings as any).filedForm8027LastYear : false,
            customComplianceRate: settings.customComplianceRate !== undefined ? settings.customComplianceRate : 0.08,
            enableServiceChargeDistribution: settings.enableServiceChargeDistribution !== undefined ? settings.enableServiceChargeDistribution : true,
            serviceChargeEmployeeShare: settings.serviceChargeEmployeeShare !== undefined ? settings.serviceChargeEmployeeShare : 100,
            serviceChargeAllocationMethod: settings.serviceChargeAllocationMethod || 'direct_server',
            enableMerchantFeeDeduction: settings.enableMerchantFeeDeduction !== undefined ? settings.enableMerchantFeeDeduction : false,
            merchantFeeRate: settings.merchantFeeRate !== undefined ? settings.merchantFeeRate : 2.25
          }));
        }
        
        const [payoutConfig, payCycleConfig] = await Promise.all([
          getPlatformConfig('payoutMethods'),
          getPlatformConfig('payCycles')
        ]);
        
        if (payoutConfig) setPayoutMethods(payoutConfig.options);
        if (payCycleConfig) setPayCycles(payCycleConfig.options);
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSettings();
  }, [tenantId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submitting settings form with:', formData);
    
    setIsSaving(true);
    setStatus('');
    try {
      // If tenant is in a prohibited state (like CA), enforce fee deduction as false
      const sanitizedMerchantFeeEnabled = isStateProhibitingFeeDeduction ? false : formData.enableMerchantFeeDeduction;

      console.log('Calling saveSettings with data:', { tenantId, ...formData });
      await saveSettings(tenantId, { 
        tenantId, 
        payoutMethod: formData.payoutMethod,
        allowPayoutOverride: formData.allowPayoutOverride,
        payCycle: formData.payCycle,
        payDay: formData.payDay,
        payDayDue: formData.payDayDue,
        payrollProvider: formData.payrollProvider,
        adpCompanyCode: (formData as any).adpCompanyCode || '',
        posMerchantProvider: formData.posMerchantProvider || '',
        posTipHandling: formData.posTipHandling as 'Unpaid' | 'Reported',
        displayTipBreakdown: formData.displayTipBreakdown,
        timezone: formData.timezone,
        usePosShiftDataForActuals: formData.usePosShiftDataForActuals,
        isPosTipAllocationRequired: formData.isPosTipAllocationRequired,
        allocationMethod: formData.allocationMethod,
        requireIRS8PercentCompliance: formData.requireIRS8PercentCompliance,
        irs8027Cadence: formData.irs8027Cadence,
        businessIndustry: formData.businessIndustry,
        employeeCountOver10: formData.employeeCountOver10,
        filedForm8027LastYear: (formData as any).filedForm8027LastYear,
        customComplianceRate: formData.customComplianceRate,
        anchorDate: formData.anchorDate || '',
        enableServiceChargeDistribution: formData.enableServiceChargeDistribution,
        serviceChargeEmployeeShare: formData.serviceChargeEmployeeShare,
        serviceChargeAllocationMethod: formData.serviceChargeAllocationMethod,
        enableMerchantFeeDeduction: sanitizedMerchantFeeEnabled,
        merchantFeeRate: formData.merchantFeeRate,
        posMapping: formData.posMapping
      });
      setStatus('Settings saved successfully!');
      setTimeout(() => setStatus(''), 3000);
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setStatus('Error saving settings.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="card p-12 flex flex-col items-center justify-center space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="text-sm text-gray-500 font-medium">Loading settings...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h2 className="text-xl md:text-3xl font-black text-gray-900 tracking-tight uppercase font-sans">General Settings</h2>
        <p className="text-gray-500 mt-1 font-medium text-xs md:text-sm">Configure your POS integration, payroll preferences, service fees, and IRS compliance rules.</p>
      </div>

      {/* Sub-tabs Navigation */}
      <div className="flex flex-wrap gap-2 bg-gray-100/90 p-1.5 rounded-2xl w-full border border-gray-200/60">
        <button
          type="button"
          onClick={() => setActiveSubTab('pos')}
          className={`flex-1 min-w-[130px] py-2.5 px-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 ${
            activeSubTab === 'pos'
              ? 'bg-white text-blue-700 shadow-xs ring-1 ring-black/5'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200/50'
          }`}
        >
          <CreditCard size={15} className={activeSubTab === 'pos' ? 'text-blue-600' : 'text-gray-400'} />
          <span>POS Settings</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('fees')}
          className={`flex-1 min-w-[150px] py-2.5 px-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 ${
            activeSubTab === 'fees'
              ? 'bg-white text-blue-700 shadow-xs ring-1 ring-black/5'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200/50'
          }`}
        >
          <Percent size={15} className={activeSubTab === 'fees' ? 'text-blue-600' : 'text-gray-400'} />
          <span>Service Fees & CC Offsets</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('payroll')}
          className={`flex-1 min-w-[130px] py-2.5 px-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 ${
            activeSubTab === 'payroll'
              ? 'bg-white text-blue-700 shadow-xs ring-1 ring-black/5'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200/50'
          }`}
        >
          <Banknote size={15} className={activeSubTab === 'payroll' ? 'text-blue-600' : 'text-gray-400'} />
          <span>Payroll Settings</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('compliance')}
          className={`flex-1 min-w-[170px] py-2.5 px-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 ${
            activeSubTab === 'compliance'
              ? 'bg-white text-blue-700 shadow-xs ring-1 ring-black/5'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200/50'
          }`}
        >
          <Scale size={15} className={activeSubTab === 'compliance' ? 'text-blue-600' : 'text-gray-400'} />
          <span>IRS Form 8027</span>
          {formData.requireIRS8PercentCompliance && (
            <span className="w-2 h-2 rounded-full bg-emerald-500 ml-1"></span>
          )}
        </button>
      </div>

      {/* Tab Panels */}
      <div className="space-y-6">
        <AnimatePresence mode="wait">
          {activeSubTab === 'pos' && (
            <motion.div
              key="pos-tab"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
              className="bg-gray-50 p-6 md:p-8 rounded-3xl border border-gray-100 space-y-6"
            >
              <div className="flex items-center gap-2.5 pb-4 border-b border-gray-200/60">
                <span className="p-2 bg-blue-50 text-blue-700 rounded-xl">
                  <CreditCard size={18} />
                </span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest">POS Integration & Tip Handling</h3>
                  <p className="text-xs text-gray-400 font-medium">Control how credit card tips and shift hours from your POS are processed</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Credit Card (POS) Tip Handling</label>
                  <select 
                    value={formData.posTipHandling} 
                    onChange={e => setFormData({...formData, posTipHandling: e.target.value as 'Unpaid' | 'Reported'})} 
                    className="input-field"
                  >
                    <option value="Reported">Reported (Excluded from payroll extract / already paid out)</option>
                    <option value="Unpaid">Unpaid (Included in payroll extract for paycheck payment)</option>
                  </select>
                  <p className="text-[11px] text-gray-400 italic">Determines whether POS tips are included as payable items in the payroll extract.</p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">POS Merchant Provider</label>
                  <select 
                    value={formData.posMerchantProvider} 
                    onChange={e => setFormData({...formData, posMerchantProvider: e.target.value})} 
                    className="input-field"
                  >
                    <option value="">Select POS Provider</option>
                    <option>Toast</option>
                    <option>Square</option>
                    <option>Clover</option>
                    <option>Lightspeed</option>
                    <option>SpotOn</option>
                    <option>Other</option>
                  </select>
                  <p className="text-[11px] text-gray-400 italic">Identifies your point-of-sale system for report format matching.</p>
                </div>

                <div className="space-y-3 md:col-span-2 pt-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block">POS Shift / Hours Worked Usage</label>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 p-4 bg-white rounded-2xl border border-gray-200 cursor-pointer hover:border-blue-300 transition-all shadow-2xs">
                      <input 
                        type="radio"
                        name="usePosShiftDataForActuals"
                        checked={formData.usePosShiftDataForActuals === true}
                        onChange={() => setFormData({...formData, usePosShiftDataForActuals: true})}
                        className="w-5 h-5 mt-0.5 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <div>
                        <span className="text-sm font-bold text-gray-800">Use POS Shift Data (Hours Worked) for Actuals</span>
                        <p className="text-xs text-gray-500 mt-0.5">Dynamically adjusts tip allocations for partial shifts and actual clock-in/out hours.</p>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 p-4 bg-white rounded-2xl border border-gray-200 cursor-pointer hover:border-blue-300 transition-all shadow-2xs">
                      <input 
                        type="radio"
                        name="usePosShiftDataForActuals"
                        checked={formData.usePosShiftDataForActuals === false}
                        onChange={() => setFormData({...formData, usePosShiftDataForActuals: false})}
                        className="w-5 h-5 mt-0.5 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <div>
                        <span className="text-sm font-bold text-gray-800">Ignore POS Shift Data (Flat Scheduled Allocations)</span>
                        <p className="text-xs text-gray-500 mt-0.5">Do not adjust allocations based on partial shift hours worked.</p>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 p-4 bg-white/60 rounded-2xl border border-gray-200/80 cursor-not-allowed">
                      <input 
                        type="checkbox"
                        disabled
                        checked={!!formData.isPosTipAllocationRequired}
                        onChange={(e) => setFormData({...formData, isPosTipAllocationRequired: e.target.checked})}
                        className="w-5 h-5 text-blue-600 focus:ring-blue-500 rounded border-gray-300 disabled:opacity-50 cursor-not-allowed"
                      />
                      <div>
                        <span className="text-sm text-gray-400">Require Allocation for POS Tips</span>
                        <p className="text-[10px] text-gray-400">Managed via Role Percentage Configuration in the Allocations tab.</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* DEDICATED SERVICE CHARGES & MERCHANT FEES TAB */}
          {activeSubTab === 'fees' && (
            <motion.div
              key="fees-tab"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
              className="bg-gray-50 p-6 md:p-8 rounded-3xl border border-gray-100 space-y-6"
            >
              <div className="flex items-center justify-between pb-4 border-b border-gray-200/60">
                <div className="flex items-center gap-2.5">
                  <span className="p-2 bg-indigo-50 text-indigo-700 rounded-xl">
                    <Percent size={18} />
                  </span>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest">Service Charges & Credit Card Fees</h3>
                    <p className="text-xs text-gray-400 font-medium">Configure auto-gratuity distributions, house retentions, and state-governed merchant fee offsets</p>
                  </div>
                </div>

                {tenantState && (
                  <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-xl border border-gray-200 text-xs font-bold text-gray-700">
                    <MapPin size={13} className="text-blue-600" />
                    <span>Jurisdiction: {tenantState}</span>
                  </div>
                )}
              </div>

              {/* State Jurisdiction Status Card */}
              {!tenantState ? (
                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200/80 flex gap-3 text-amber-900">
                  <Info size={20} className="text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-xs leading-normal">
                    <span className="font-bold">Business State Missing:</span> We could not detect your physical state jurisdiction. Tip deduction rules and FLSA compliance depend on your operating state.
                    <button 
                      type="button" 
                      onClick={() => navigate('/settings/onboarding')}
                      className="ml-2 font-bold underline text-amber-950 hover:text-amber-700 cursor-pointer"
                    >
                      Set Address in Business Profile →
                    </button>
                  </div>
                </div>
              ) : isStateProhibitingFeeDeduction ? (
                <div className="p-4 bg-blue-50/90 rounded-2xl border border-blue-200 flex gap-3 text-blue-950">
                  <ShieldCheck size={20} className="text-blue-600 shrink-0 mt-0.5" />
                  <div className="text-xs leading-normal">
                    <span className="font-bold">{tenantState} State Labor Protection Active:</span> Under {tenantState === 'CA' ? 'California Labor Code § 351' : `${tenantState} state labor statutes`}, gratuities are the sole property of the employee. Employers are strictly prohibited from deducting credit card merchant processing fees or surcharge offsets from employee tips.
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-emerald-50/80 rounded-2xl border border-emerald-200 flex gap-3 text-emerald-950">
                  <ShieldCheck size={20} className="text-emerald-600 shrink-0 mt-0.5" />
                  <div className="text-xs leading-normal">
                    <span className="font-bold">{tenantState} FLSA Standard Applied:</span> Under Federal FLSA safe-harbors, employers operating in {tenantState} may deduct actual transactional merchant processing fees incurred on charged tips, provided net compensation complies with statutory minimum wages.
                  </div>
                </div>
              )}

              {/* CARD 1: SERVICE CHARGES & AUTO-GRATUITIES */}
              <div className="bg-white p-6 rounded-2xl border border-gray-200/80 shadow-2xs space-y-5">
                <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                  <div className="space-y-0.5">
                    <span className="text-sm font-bold text-gray-800 uppercase tracking-tight">Distribute Service Charges / Auto-Gratuities to Staff</span>
                    <p className="text-xs text-gray-400">When enabled, imported POS service fees are treated as distributable compensation.</p>
                  </div>
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      id="enableServiceChargeDistributionToggle"
                      checked={formData.enableServiceChargeDistribution}
                      onChange={e => setFormData({...formData, enableServiceChargeDistribution: e.target.checked})}
                    />
                    <label htmlFor="enableServiceChargeDistributionToggle" className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 block cursor-pointer"></label>
                  </div>
                </div>

                {formData.enableServiceChargeDistribution ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-top-2 duration-200">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Employee Payout Share (%)</label>
                      <div className="relative">
                        <input 
                          type="number"
                          min="1"
                          max="100"
                          step="1"
                          value={formData.serviceChargeEmployeeShare}
                          onChange={e => setFormData({...formData, serviceChargeEmployeeShare: Math.min(100, Math.max(0, parseInt(e.target.value) || 0))})}
                          className="input-field pr-8 font-bold text-gray-900"
                        />
                        <span className="absolute right-3 top-2.5 text-xs text-gray-400 font-bold">%</span>
                      </div>
                      <p className="text-[11px] text-gray-400">
                        {formData.serviceChargeEmployeeShare === 100 
                          ? '100% of service charges are paid out to staff (0% retained by house).' 
                          : `${formData.serviceChargeEmployeeShare}% paid to staff, ${100 - formData.serviceChargeEmployeeShare}% retained by house for administrative / catering costs.`}
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Allocation Strategy</label>
                      <select 
                        value={formData.serviceChargeAllocationMethod} 
                        onChange={e => setFormData({...formData, serviceChargeAllocationMethod: e.target.value as 'direct_server' | 'shift_pool'})} 
                        className="input-field"
                      >
                        <option value="direct_server">Direct to Primary Server on Check</option>
                        <option value="shift_pool">Pool across Shift Staff (via Role Percentages)</option>
                      </select>
                      <p className="text-[11px] text-gray-400">
                        {formData.serviceChargeAllocationMethod === 'direct_server' 
                          ? 'Passes the employee share directly to the individual server associated with the check in the POS.'
                          : 'Adds the employee share to the shift tip pool to be distributed based on role percentages and hours worked.'}
                      </p>
                    </div>

                    <div className="md:col-span-2 p-3.5 bg-gray-50 rounded-xl border border-gray-200/70 text-[11px] text-gray-600 flex items-start gap-2">
                      <Info size={16} className="text-gray-400 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-bold text-gray-700">IRS Rev. Rul. 2012-18 Notice:</span> Mandatory service charges distributed to employees are legally classified by the IRS as non-tip wages rather than voluntary tips, and must be reported as taxable W-2 wages.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 rounded-xl text-xs text-gray-500 italic">
                    Service charges and auto-gratuities are retained 100% by the business and excluded from employee tip calculations and payroll extracts.
                  </div>
                )}
              </div>

              {/* CARD 2: MERCHANT PROCESSING FEE DEDUCTION (STATE GATED) */}
              <div className={`p-6 rounded-2xl border transition-all ${
                isStateProhibitingFeeDeduction 
                  ? 'bg-gray-100/70 border-gray-200 text-gray-400' 
                  : 'bg-white border-gray-200/80 shadow-2xs'
              }`}>
                <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-800 uppercase tracking-tight">Deduct Credit Card Processing Fee on Tips</span>
                      {isStateProhibitingFeeDeduction && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">
                          <Lock size={10} /> Locked by {tenantState || 'State'} Law
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">
                      Offset actual third-party merchant interchange fees incurred when processing charged tips.
                    </p>
                  </div>
                  
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      id="enableMerchantFeeDeductionToggle"
                      disabled={isStateProhibitingFeeDeduction}
                      checked={isStateProhibitingFeeDeduction ? false : formData.enableMerchantFeeDeduction}
                      onChange={e => setFormData({...formData, enableMerchantFeeDeduction: e.target.checked})}
                    />
                    <label 
                      htmlFor="enableMerchantFeeDeductionToggle" 
                      className={`w-11 h-6 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all block ${
                        isStateProhibitingFeeDeduction 
                          ? 'bg-gray-200 cursor-not-allowed opacity-60' 
                          : 'bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 peer-checked:bg-blue-600 cursor-pointer'
                      }`}
                    ></label>
                  </div>
                </div>

                {isStateProhibitingFeeDeduction ? (
                  <div className="mt-4 p-3.5 bg-amber-50/80 rounded-xl border border-amber-200/80 text-amber-900 text-xs flex gap-2.5 items-start">
                    <Lock size={16} className="text-amber-700 shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold">Feature Restricted in {tenantState}:</span> California Labor Code § 351 (and applicable state statutes) explicitly forbids any employer from making deductions from employee gratuities for credit card processing charges or handling costs.
                    </div>
                  </div>
                ) : formData.enableMerchantFeeDeduction ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 animate-in slide-in-from-top-2 duration-200">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Actual Transaction Fee Rate (%)</label>
                      <div className="relative">
                        <input 
                          type="number"
                          min="0.5"
                          max="5.0"
                          step="0.01"
                          value={formData.merchantFeeRate}
                          onChange={e => setFormData({...formData, merchantFeeRate: parseFloat(e.target.value) || 0})}
                          className="input-field pr-8 font-bold text-gray-900"
                        />
                        <span className="absolute right-3 top-2.5 text-xs text-gray-400 font-bold">%</span>
                      </div>
                      <p className="text-[11px] text-gray-400">FLSA requires that this cannot exceed the actual processing rate charged by the merchant processor (typically 2.0% – 2.75%).</p>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Compliance Rule</label>
                      <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-[11px] text-gray-600 space-y-1">
                        <span className="font-bold text-gray-700 block">Federal FLSA Guidelines:</span>
                        Fee deduction is applied strictly to charged tip totals and never to baseline wages or cash tips. Net compensation must satisfy statutory minimum hourly wages.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-gray-400 italic">
                    Employer absorbs 100% of merchant processing fees on credit card tips.
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeSubTab === 'payroll' && (
            <motion.div
              key="payroll-tab"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
              className="bg-gray-50 p-6 md:p-8 rounded-3xl border border-gray-100 space-y-6"
            >
              <div className="flex items-center gap-2.5 pb-4 border-b border-gray-200/60">
                <span className="p-2 bg-emerald-50 text-emerald-700 rounded-xl">
                  <Banknote size={18} />
                </span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest">Payroll & Schedule Settings</h3>
                  <p className="text-xs text-gray-400 font-medium">Configure pay frequency, payout methods, anchor dates, and timezone</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Payroll Provider</label>
                  <select 
                    value={formData.payrollProvider} 
                    onChange={e => setFormData({...formData, payrollProvider: e.target.value})} 
                    className="input-field"
                  >
                    <option value="">Select Provider</option>
                    <option>Gusto</option>
                    <option>ADP</option>
                    <option>Rippling</option>
                    <option>Paychex</option>
                    <option>Justworks</option>
                    <option>Other</option>
                  </select>
                </div>

                {formData.payrollProvider === 'ADP' && (
                  <div className="space-y-1.5 animate-in slide-in-from-left-2 duration-300">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">ADP Company Code</label>
                    <input 
                      type="text"
                      value={(formData as any).adpCompanyCode || ''}
                      onChange={e => setFormData({...formData, adpCompanyCode: e.target.value} as any)}
                      placeholder="e.g., ABC-123"
                      className="input-field"
                    />
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Default Payout Method</label>
                  <select 
                    value={formData.payoutMethod} 
                    onChange={e => setFormData({...formData, payoutMethod: e.target.value})} 
                    className="input-field"
                  >
                    {payoutMethods.map((method, index) => <option key={`${method}-${index}`} value={method}>{method}</option>)}
                  </select>
                  <p className="text-[11px] text-gray-400 italic">Fallback method if no override is provided.</p>
                </div>

                <div className="space-y-1.5 flex flex-col justify-center">
                  <label className="flex items-center gap-3 cursor-pointer group bg-white p-3.5 rounded-2xl border border-gray-200">
                    <div className="relative">
                      <input 
                        type="checkbox" 
                        className="sr-only peer"
                        checked={formData.allowPayoutOverride}
                        onChange={e => setFormData({...formData, allowPayoutOverride: e.target.checked})}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </div>
                    <div>
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-tight">Allow Payout Overrides</span>
                      <p className="text-[10px] text-gray-400">Enable selecting payout method per tip submission.</p>
                    </div>
                  </label>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Pay Cycle</label>
                  <select 
                    value={formData.payCycle} 
                    onChange={e => setFormData({...formData, payCycle: e.target.value})} 
                    className="input-field"
                  >
                    {payCycles.map((cycle, index) => <option key={`${cycle}-${index}`} value={cycle}>{cycle}</option>)}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Pay Day</label>
                  <select 
                    value={formData.payDay} 
                    onChange={e => setFormData({...formData, payDay: e.target.value})} 
                    className="input-field" 
                  >
                    <option value="">Select Day</option>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => <option key={day} value={day}>{day}</option>)}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Payroll Due Day</label>
                  <select 
                    value={formData.payDayDue} 
                    onChange={e => setFormData({...formData, payDayDue: e.target.value})} 
                    className="input-field" 
                  >
                    <option value="">Select Day</option>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => <option key={day} value={day}>{day}</option>)}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Timezone</label>
                  <select 
                    value={formData.timezone} 
                    onChange={e => setFormData({...formData, timezone: e.target.value})} 
                    className="input-field"
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  </select>
                </div>

                <div className="space-y-1.5 md:col-span-2">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Payroll Anchor Date (Cycle Start Date)</label>
                  <input 
                    type="date"
                    value={formData.anchorDate || ''}
                    onChange={e => setFormData({...formData, anchorDate: e.target.value})}
                    className="input-field"
                  />
                  <p className="text-[11px] text-gray-400 italic">Select the start date of your first pay period. Future pay periods generate dynamically from this anchor date to maintain gap-free records.</p>
                </div>
              </div>
            </motion.div>
          )}

          {activeSubTab === 'compliance' && (
            <motion.div
              key="compliance-tab"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15 }}
              className="bg-gray-50 p-6 md:p-8 rounded-3xl border border-gray-100 space-y-6"
            >
              <div className="flex items-center gap-2.5 pb-4 border-b border-gray-200/60">
                <span className="p-2 bg-purple-50 text-purple-700 rounded-xl">
                  <Scale size={18} />
                </span>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest">IRS Form 8027 & Safe-Harbor Compliance</h3>
                  <p className="text-xs text-gray-400 font-medium">Configure Section 6053(c) tip threshold assessments and shortfall true-ups</p>
                </div>
              </div>
              
              <p className="text-xs text-gray-500 leading-relaxed bg-white p-4 rounded-2xl border border-gray-200/70">
                Section 6053(c) requires "Large Food or Beverage Establishments" to monitor employee tips and ensure they total at least 8% of gross receipts. If reported tips fall below 8%, the employer is legally required to allocate the shortfall amongst tipped staff and report it on Form 8027.
              </p>

              {/* Compliance Self-Assessment Box */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-6 rounded-2xl border border-gray-200/80 shadow-2xs">
                <div className="space-y-1.5 md:col-span-2">
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">Compliance Self-Assessment</h4>
                  <p className="text-[11px] text-gray-400 italic">Answer these questions to see if your business is legally subject to Form 8027 regulations.</p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Business/Industry Type</label>
                  <select 
                    value={formData.businessIndustry} 
                    onChange={e => setFormData({...formData, businessIndustry: e.target.value})} 
                    className="input-field"
                  >
                    <option value="Restaurant">Restaurant, Cafe, or Dining</option>
                    <option value="Bar">Bar, Tavern, or Nightclub</option>
                    <option value="Hotel">Hotel or Resort Food Service</option>
                    <option value="Hospitality">Other Tipped Hospitality (Salons, Valet, Spas)</option>
                    <option value="Other">Other (General Retail/Service)</option>
                  </select>
                </div>

                <div className="space-y-1.5 flex flex-col justify-center">
                  <label className="flex items-center gap-3 cursor-pointer group p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <input 
                      type="checkbox" 
                      className="w-5 h-5 text-blue-600 focus:ring-blue-500 rounded border-gray-300 cursor-pointer"
                      checked={formData.employeeCountOver10}
                      onChange={e => setFormData({...formData, employeeCountOver10: e.target.checked})}
                    />
                    <div>
                      <span className="text-xs font-bold text-gray-700">Employed &gt; 10 staff last year?</span>
                      <p className="text-[10px] text-gray-400">Normally employed 10+ people on a typical business day.</p>
                    </div>
                  </label>
                </div>

                <div className="space-y-1.5 flex flex-col justify-center md:col-span-2 border-t border-gray-100 pt-4">
                  <label className="flex items-center gap-3 cursor-pointer group p-3 bg-gray-50 rounded-xl border border-gray-100">
                    <input 
                      type="checkbox" 
                      className="w-5 h-5 text-blue-600 focus:ring-blue-500 rounded border-gray-300 cursor-pointer"
                      checked={(formData as any).filedForm8027LastYear}
                      onChange={e => setFormData({...formData, filedForm8027LastYear: e.target.checked})}
                    />
                    <div>
                      <span className="text-xs font-bold text-gray-700">Did your business file IRS Form 8027 last year?</span>
                      <p className="text-[10px] text-gray-400">Or does your tax advisor determine that you are required to file this year?</p>
                    </div>
                  </label>
                </div>

                {/* Recommendation alert */}
                <div className="md:col-span-2">
                  {(formData as any).filedForm8027LastYear ? (
                    <div className="p-4 bg-amber-50 rounded-xl border border-amber-200/80 flex gap-3 text-amber-900">
                      <span className="text-base leading-none">💡</span>
                      <div className="text-xs leading-normal">
                        <span className="font-bold">Recommendation: IRS Compliance Required.</span> Based on your confirmation that you filed Form 8027 last year (or are required to file), your business is <span className="font-bold underline">subject to Form 8027 reporting</span>. You should enable the 8% safe-harbor tracking to manage allocations.
                      </div>
                    </div>
                  ) : (formData.businessIndustry === 'Restaurant' || formData.businessIndustry === 'Bar' || formData.businessIndustry === 'Hotel') && formData.employeeCountOver10 ? (
                    <div className="p-4 bg-amber-50 rounded-xl border border-amber-200/80 flex gap-3 text-amber-900">
                      <span className="text-base leading-none">💡</span>
                      <div className="text-xs leading-normal">
                        <span className="font-bold">Recommendation: IRS Compliance Highly Likely.</span> Based on your industry (food/beverage) and staffing size (&gt; 10 employees), your business satisfies the IRS statutory definition of a "Large Food or Beverage Establishment" under Section 6053(c). You should consult with your legal/tax counsel to verify if Form 8027 filing is required.
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-200/80 flex gap-3 text-blue-900">
                      <span className="text-base leading-none">ℹ️</span>
                      <div className="text-xs leading-normal">
                        <span className="font-bold">Recommendation: Voluntary Tracking.</span> Based on your selections, Form 8027 reporting may not be legally mandatory for your establishment. However, you can still enable shortfall monitoring to run internal audits, track safe-harbor compliance, and safeguard payroll thresholds.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Shortfall Tracking Toggle Card */}
              <div className="space-y-4 bg-white p-6 rounded-2xl border border-gray-200/80 shadow-2xs">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <span className="text-sm font-bold text-gray-800 uppercase tracking-tight">Enable Form 8027 Shortfall Tracking</span>
                    <p className="text-xs text-gray-400">Activates gross receipts importing and safe-harbor true-up allocations.</p>
                  </div>
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      className="sr-only peer"
                      id="requireIRS8PercentComplianceToggle"
                      checked={formData.requireIRS8PercentCompliance}
                      onChange={e => setFormData({...formData, requireIRS8PercentCompliance: e.target.checked})}
                    />
                    <label htmlFor="requireIRS8PercentComplianceToggle" className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 block cursor-pointer"></label>
                  </div>
                </div>

                {formData.requireIRS8PercentCompliance && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-100 animate-in slide-in-from-top-2 duration-350">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Compliance Review Cadence</label>
                      <select 
                        value={formData.irs8027Cadence || 'monthly'}
                        onChange={e => setFormData({...formData, irs8027Cadence: e.target.value as any})}
                        className="input-field"
                      >
                        <option value="pay_cycle">Every Pay Period</option>
                        <option value="monthly">Monthly Review</option>
                        <option value="quarterly">Quarterly Review (Calendar Quarters)</option>
                        <option value="annually">Annual Reconciliation (IRS Form 8027)</option>
                      </select>
                      <p className="text-[10px] text-gray-400">Sets the scheduled review frequency for Gross Receipts & Safe-Harbor True-Ups.</p>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Safe-Harbor Compliance Rate (%)</label>
                      <div className="relative">
                        <input 
                          type="number"
                          min="2"
                          max="20"
                          step="0.1"
                          value={formData.customComplianceRate * 100}
                          onChange={e => setFormData({...formData, customComplianceRate: parseFloat(e.target.value) / 100})}
                          className="input-field pr-8"
                        />
                        <span className="absolute right-3 top-2.5 text-xs text-gray-400 font-bold">%</span>
                      </div>
                      <p className="text-[10px] text-gray-400">The IRS standard is 8.0%. Minimum petitionable rate is 2.0%.</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Global Save Button & Feedback Status */}
        <div className="pt-2">
          <button 
            type="submit" 
            disabled={isSaving}
            className="btn-primary w-full py-4 rounded-2xl shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 cursor-pointer font-bold uppercase tracking-wider text-xs"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving Settings...</span>
              </>
            ) : (
              <span>Save Settings</span>
            )}
          </button>

          {status && (
            <div className={`mt-4 p-4 rounded-2xl text-center font-bold text-xs uppercase tracking-wider animate-in fade-in slide-in-from-top-2 flex items-center justify-center gap-2 ${
              status.includes('successfully') ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/80' : 'bg-red-50 text-red-700 border border-red-200/80'
            }`}>
              {status.includes('successfully') ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
              <span>{status}</span>
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

